import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import {
  BRAND_MODAL_PAPER_PROPS,
  BRAND_MODAL_TITLE_SX,
} from "../../../shared/components/modalStyles";
import { getProspectingTagDescription } from "./prospectingTagMetadata";

const BRAND_GREEN = "#648315";
const BRAND_BLUE = "#00336C";
const SOFT_BLUE = "#EEF4FB";

const PROSPECTING_TAG_OPTIONS = [
  "Existing Employer",
  "Stakeholder",
  "Investor / Developer",
  "Site Selector / Consultant",
  "Other EDO / Municipality",
  "Media / Researcher",
  "High Priority",
  "Target Industry",
  "Follow-Up",
];

function normalizeTags(value) {
  if (!Array.isArray(value)) return [];

  const seen = new Set();
  const next = [];

  for (const raw of value) {
    const tag = String(raw || "").trim();
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);
    next.push(tag);
  }

  return next;
}

function tagsEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }

  return true;
}

export default function ProspectingTagEditor({
  entityId,
  userTags,
  autoTags = [],
  onCommit,
  onTagsUpdated,
  layout = "column",
}) {
  const normalizedAutoTags = useMemo(() => normalizeTags(autoTags), [autoTags]);
  const normalizedTagOptions = useMemo(
    () => normalizeTags(PROSPECTING_TAG_OPTIONS),
    [],
  );

  const [localUserTags, setLocalUserTags] = useState(() =>
    normalizeTags(userTags),
  );
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftTags, setDraftTags] = useState([]);

  const latestCommittedTagsRef = useRef(normalizeTags(userTags));
  const requestIdRef = useRef(0);

  useEffect(() => {
    const next = normalizeTags(userTags);
    if (tagsEqual(next, latestCommittedTagsRef.current)) return;

    latestCommittedTagsRef.current = next;
    setLocalUserTags((current) => (tagsEqual(current, next) ? current : next));
  }, [userTags]);

  const handleTagChange = async (nextTags) => {
    if (!entityId || typeof onCommit !== "function") return;

    const normalized = normalizeTags(nextTags);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setLocalUserTags(normalized);
    onTagsUpdated?.(normalized);
    setSaving(true);

    try {
      const result = await onCommit(normalized);
      if (requestId !== requestIdRef.current) return;

      const resolvedUserTags = normalizeTags(result ?? normalized);
      latestCommittedTagsRef.current = resolvedUserTags;
      setLocalUserTags(resolvedUserTags);
      onTagsUpdated?.(resolvedUserTags);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;

      setLocalUserTags(latestCommittedTagsRef.current);
      onTagsUpdated?.(latestCommittedTagsRef.current);
      console.error("Failed to update tags", err);
    } finally {
      if (requestId === requestIdRef.current) {
        setSaving(false);
      }
    }
  };

  const openEditor = (e) => {
    e.stopPropagation();
    setDraftTags([...localUserTags]);
    setEditing(true);
  };

  const handleDone = () => {
    setEditing(false);
    if (!tagsEqual(draftTags, localUserTags)) {
      handleTagChange(draftTags);
    }
  };

  const toggleDraft = (tag) => {
    setDraftTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const rowLayout = layout === "row";

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={
        rowLayout
          ? {
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "center",
              alignItems: "center",
              gap: 0.5,
              minHeight: 28,
              width: "100%",
            }
          : {
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.5,
              minHeight: 28,
              width: "100%",
            }
      }
    >
      {normalizedAutoTags.map((tag) => (
        <Tooltip key={`auto-${tag}`} title={getProspectingTagDescription(tag)} arrow>
          <Chip
            label={tag}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 700,
              color: BRAND_BLUE,
              backgroundColor: SOFT_BLUE,
            }}
          />
        </Tooltip>
      ))}

      {localUserTags.map((tag) => (
        <Tooltip key={`user-${tag}`} title={getProspectingTagDescription(tag)} arrow>
          <Chip
            label={tag}
            size="small"
            onDelete={
              saving
                ? undefined
                : (e) => {
                    e.stopPropagation();
                    handleTagChange(localUserTags.filter((item) => item !== tag));
                  }
            }
            sx={{
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 700,
              backgroundColor: BRAND_GREEN,
              color: "white",
              "& .MuiChip-deleteIcon": {
                color: "rgba(255,255,255,0.9)",
                fontSize: 18,
                "&:hover": { color: "white" },
              },
            }}
          />
        </Tooltip>
      ))}

      {!editing && (
        <Chip
          label="+"
          size="small"
          onClick={openEditor}
          sx={{
            height: 22,
            fontSize: "0.7rem",
            fontWeight: 700,
            backgroundColor: "transparent",
            border: `1px dashed ${BRAND_GREEN}`,
            color: BRAND_GREEN,
            cursor: "pointer",
          }}
        />
      )}

      {saving && (
        <CircularProgress size={14} sx={{ color: BRAND_GREEN, ml: 0.5 }} />
      )}

      <Dialog
        open={editing}
        onClose={() => setEditing(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={BRAND_MODAL_PAPER_PROPS}
      >
        <DialogTitle sx={{ ...BRAND_MODAL_TITLE_SX, pb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LocalOfferOutlinedIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.8)" }} />
            <span>Select Tags</span>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: "white", pt: 2.5, pb: 1 }}>
          {draftTags.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: "#94A3B8",
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                  mb: 1,
                }}
              >
                Selected
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {draftTags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    icon={<CheckIcon sx={{ fontSize: "14px !important" }} />}
                    onClick={() => toggleDraft(tag)}
                    sx={{
                      height: 26,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      backgroundColor: BRAND_GREEN,
                      color: "white",
                      "& .MuiChip-icon": { color: "rgba(255,255,255,0.9)" },
                      "&:hover": { backgroundColor: "#556f12" },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Typography
            sx={{
              fontSize: "0.65rem",
              fontWeight: 700,
              color: "#94A3B8",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              mt: draftTags.length > 0 ? 2 : 1.5,
              mb: 1,
            }}
          >
            {draftTags.length > 0 ? "Available" : "Choose tags"}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {normalizedTagOptions
              .filter((tag) => !draftTags.includes(tag))
              .map((tag) => {
                const description = getProspectingTagDescription(tag);
                return (
                  <Tooltip key={tag} title={description} arrow placement="top">
                    <Chip
                      label={tag}
                      size="small"
                      onClick={() => toggleDraft(tag)}
                      sx={{
                        height: 26,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        backgroundColor: "#F1F5F9",
                        color: "#334155",
                        border: "1.5px solid #E2E8F0",
                        "&:hover": {
                          backgroundColor: SOFT_BLUE,
                          borderColor: "#93C5FD",
                          color: BRAND_BLUE,
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            backgroundColor: "white",
            borderTop: "1px solid #F1F5F9",
            px: 2,
            py: 1.25,
            gap: 1,
          }}
        >
          <Button
            onClick={() => setEditing(false)}
            size="small"
            sx={{
              color: "#64748B",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#F8FAFC" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDone}
            size="small"
            variant="contained"
            sx={{
              backgroundColor: BRAND_GREEN,
              color: "white",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 1.5,
              px: 2.5,
              "&:hover": { backgroundColor: "#556f12" },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
